import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Store banne bloqué' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Store banne bloqué dans l\'Hérault : que faire',
  description: 'Votre store banne est bloqué ? Moteur, télécommande, bras, fin de course, toile : causes et dépannage. Les gestes à éviter et l\'entretien adapté au climat de l\'Hérault (soleil, vent).',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/store-banne-bloque-que-faire/',
  datePublished: '2026-03-02',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Que faire si mon store banne est bloqué en position déployée ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Si le store est électrique, vérifiez d\'abord la télécommande (piles) et le disjoncteur. Si le moteur ne répond pas, coupez l\'alimentation électrique et ne forcez jamais sur les bras ou la toile pour tenter de le replier manuellement — vous risqueriez de tordre les bras ou de déchirer la toile. Contactez un professionnel.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pourquoi mon store banne ne se rétracte plus ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les causes les plus fréquentes sont : un condensateur de moteur HS, un fin de course déréglé, un bras articulé bloqué ou endommagé, ou une toile sortie de son tube d\'enroulement. Seul un diagnostic visuel permet de déterminer précisément l\'origine de la panne.',
      },
    },
    {
      '@type': 'Question',
      name: 'Comment entretenir un store banne dans l\'Hérault ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Nettoyez la toile une à deux fois par an à l\'eau savonneuse douce, rincez abondamment. Vérifiez régulièrement que les bras articulés ne présentent pas de jeu et que la toile s\'enroule correctement. En cas de fort vent, repliez systématiquement le store. Évitez de le laisser déployé en plein soleil quand vous n\'êtes pas là.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/store-banne-bloque-que-faire/', name: 'Store banne bloqué' } },
  ],
};

export default function ArticleStoreBanneBloque() {
  return (
    <>
      <SEO
        heroImage="/images/blog-store-banne.jpg"
        title="Store banne bloqué : causes et dépannage"
        description="Store banne bloqué dans l'Hérault ? Moteur, télécommande, bras, toile : causes et solutions. Dépannage 7j/7, devis gratuit. DFPRO Hérault."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/store-banne-bloque-que-faire/"
      />
      <BlogArticleLayout
        title="Store banne bloqué dans l&apos;Hérault : que faire"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="2 mars 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Un store banne, c&apos;est le plaisir de l&apos;ombre en terrasse, la protection contre le soleil de l&apos;Hérault — et parfois, une source de tracas quand il refuse de se plier ou de se déployer. Store bloqué en position ouverte à l&apos;approche d&apos;un coup de vent, store qui ne veut plus sortir au premier jour de beau temps : ces situations sont plus fréquentes qu&apos;on ne le croit, surtout après un hiver d&apos;inactivité ou une exposition prolongée au soleil et aux embruns sur le littoral. Voyons les causes possibles et les bons réflexes à adopter.
        </p>

        <h2>Store banne bloqué déployé : danger immédiat</h2>
        <p>
          Un store banne bloqué en position déployée est une situation à prendre au sérieux, surtout dans l&apos;Hérault où le vent — tramontane ou marin — peut se lever rapidement. Une toile déployée prise dans une rafale agit comme une voile : la pression peut tordre les bras articulés, arracher les fixations murales, ou déchirer la toile. Si votre store est bloqué ouvert, <strong>votre priorité est de le sécuriser</strong>. Si le moteur ne répond pas, coupez l&apos;alimentation électrique au disjoncteur. N&apos;essayez pas de replier manuellement un store motorisé en tirant sur la toile ou les bras : vous risquez d&apos;endommager le moteur, la toile ou les articulations. Appelez un professionnel sans tarder.
        </p>

        <h2>Panne moteur : condensateur et fins de course</h2>
        <p>
          Comme pour les volets roulants, le moteur d&apos;un store banne électrique repose sur un condensateur de démarrage qui s&apos;use avec le temps. Un store qui bourdonne mais ne bouge pas — ni dans un sens ni dans l&apos;autre — a très probablement un condensateur hors service. Le remplacement est une opération relativement simple pour un technicien, réalisée en moins d&apos;une heure.
        </p>
        <p>
          Les fins de course sont un autre point sensible. Un store banne utilise des fins de course pour savoir quand s&apos;arrêter en position complètement déployée ou complètement repliée. Un dérèglement — fréquent après des années d&apos;utilisation — peut empêcher le store de se déployer (le moteur pense qu&apos;il est déjà sorti) ou de se replier (le moteur pense qu&apos;il est déjà rentré). Le réglage des fins de course d&apos;un store nécessite d&apos;accéder au moteur, généralement situé dans le tube d&apos;enroulement, ce qui implique de démonter partiellement le coffre ou le cache-moteur.
        </p>

        {/* CTA milieu */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Store banne bloqué ? Dépannage urgent dans l&apos;Hérault
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            <Link to="/store-banne-bloque-herault/" className="text-accent-500 font-semibold hover:underline">Dépannage de store banne</Link> 7j/7. Diagnostic gratuit, devis sans engagement.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Problème de télécommande ou de récepteur</h2>
        <p>
          Avant d&apos;incriminer le moteur, vérifiez la télécommande. Une pile usée, un contact oxydé, ou une télécommande tombée dans l&apos;eau (ça arrive plus souvent qu&apos;on ne le pense, surtout près d&apos;une piscine) peut expliquer l&apos;absence de réaction du store. Testez avec une télécommande de rechange si vous en avez une, ou vérifiez que le voyant s&apos;allume quand vous appuyez.
        </p>
        <p>
          Le récepteur radio, logé dans le coffre ou à proximité du moteur, peut également être en cause. Une micro-coupure de courant peut le déprogrammer, le rendant « sourd » à la télécommande. La reprogrammation suit une procédure propre à chaque marque (Somfy, Simu, etc.). Si vous avez perdu la notice, nos techniciens peuvent reprogrammer la plupart des télécommandes en quelques minutes lors de leur intervention.
        </p>

        <h2>Bras articulés bloqués ou endommagés</h2>
        <p>
          Les bras d&apos;un store banne sont des mécanismes articulés qui se déplient et se replient à chaque cycle. Avec le temps, les axes et les points d&apos;articulation s&apos;encrassent, se corrodent ou prennent du jeu. Dans les zones littorales de l&apos;Hérault — Sète, Agde, Marseillan, La Grande-Motte — l&apos;air salin accélère la corrosion des pièces métalliques. Un bras bloqué empêche le store de se déployer ou de se replier correctement, et forcer peut tordre définitivement le mécanisme.
        </p>
        <p>
          La réparation des bras dépend de l&apos;ampleur du dommage : un simple nettoyage et graissage des articulations peut suffire si le blocage est léger ; un axe tordu ou un rivet cassé nécessite le remplacement du bras complet. Nos techniciens évaluent l&apos;état de chaque bras lors du diagnostic et vous proposent la solution la plus adaptée.
        </p>

        <h2>Toile sortie du tube ou endommagée</h2>
        <p>
          La toile du store banne s&apos;enroule autour d&apos;un tube motorisé (ou manuel). Avec l&apos;âge, les coutures peuvent céder, la toile peut se décoller du tube, ou se déchirer sous l&apos;effet du vent ou d&apos;une chute de branche. Une toile sortie de son tube d&apos;enroulement ne peut plus se replier correctement : elle s&apos;enroule de travers, crée un bourrelet qui bloque le mécanisme, ou se coince dans le coffre. Le <Link to="/reparation-store-banne-herault/" className="text-accent-500 font-semibold hover:underline">remplacement de la toile d&apos;un store banne</Link> est une opération qui se fait sur mesure, en fonction des dimensions de votre store et du type de toile (acrylique, micro-perforée, occultante). C&apos;est une alternative économique au remplacement complet du store.
        </p>

        <h2>L&apos;entretien du store banne dans le climat de l&apos;Hérault</h2>
        <p>
          Le climat méditerranéen est à la fois une bénédiction et un défi pour les stores bannes. Le soleil généreux justifie leur présence, mais les UV dégradent la toile et les composants plastiques. Le vent — tramontane ou marin — impose de la vigilance : un store déployé par vent fort est un accident en puissance. La proximité de la mer ajoute le sel à l&apos;équation, avec son effet corrosif sur les parties métalliques.
        </p>
        <p>
          Quelques gestes simples prolongent la vie de votre store. Nettoyez la toile une à deux fois par an à l&apos;eau tiède savonneuse (savon de Marseille), avec une brosse souple, et rincez abondamment. Évitez le nettoyeur haute pression, qui peut décoller les coutures ou percer la toile. En cas de vent annoncé, repliez systématiquement le store, même si le temps est beau — une rafale isolée peut faire plus de dégâts qu&apos;une averse. Enfin, ne laissez pas le store déployé en plein soleil en votre absence prolongée : l&apos;exposition continue aux UV accélère la décoloration et le vieillissement de la toile.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Peut-on motoriser un store banne manuel ?</h3>
        <p>
          Oui, la plupart des stores bannes manuels peuvent être motorisés. L&apos;opération consiste à remplacer le tube d&apos;enroulement manuel par un tube motorisé, ou à installer un moteur dans le tube existant. On ajoute une télécommande et, si souhaité, des capteurs de vent et de soleil pour une gestion automatique. C&apos;est un investissement qui apporte un confort d&apos;utilisation significatif. Nos techniciens peuvent vous conseiller sur la faisabilité technique et vous établir un devis.
        </p>

        <h3>Faut-il rentrer son store banne en hiver ?</h3>
        <p>
          Oui, c&apos;est fortement recommandé. Même replié, un store banne reste exposé aux intempéries, et le vent hivernal peut secouer la toile repliée et fatiguer les fixations. Si vous avez un store avec coffre intégral, la toile est protégée une fois repliée. Si votre store n&apos;a pas de coffre, l&apos;idéal est d&apos;investir dans un coffre de protection, ou à défaut, de vérifier régulièrement l&apos;état de la toile pendant la saison froide.
        </p>
      </BlogArticleLayout>
    </>
  );
}